import React, { useCallback, useMemo } from 'react'
import { usePanelPatientUid } from '../../dashboard/panelContext'
import { CardFooter, FieldRow, FieldSection, LabeledText } from '../fieldPrimitives'
import { SHIPPING_ADDRESS_SCHEMA } from './patientRecordSchema'
import { makeFieldSetter, useCardAutosave } from './useCardAutosave'
import { usePatientRecord } from './usePatientRecord'
import { useUpdatePatientRecord } from './useUpdatePatientRecord'

type ShippingValues = { street: string; city: string; state: string; zipCode: string }

const ShippingInfoPanel = () => {
  const uid = usePanelPatientUid()
  const { loading, patient } = usePatientRecord(uid)
  const update = useUpdatePatientRecord(uid)

  const initial = useMemo<ShippingValues>(
    () => ({
      street: patient?.shippingAddress?.street ?? '',
      city: patient?.shippingAddress?.city ?? '',
      state: patient?.shippingAddress?.state ?? '',
      zipCode: patient?.shippingAddress?.zipCode ?? '',
    }),
    [
      patient?.shippingAddress?.street,
      patient?.shippingAddress?.city,
      patient?.shippingAddress?.state,
      patient?.shippingAddress?.zipCode,
    ]
  )

  // All four fields go every time — `patientShippingAddress.upsertOne` merges nothing (README/DECISIONS).
  const save = useCallback((next: ShippingValues) => update({ shippingAddress: next }, undefined), [update])

  const { values, setValues, error, status } = useCardAutosave(initial, SHIPPING_ADDRESS_SCHEMA, save)
  const set = makeFieldSetter(setValues)

  return (
    <FieldSection>
      <LabeledText label="Shipping address" value={values.street} onChange={set('street')} />
      <FieldRow columns={3}>
        <LabeledText label="City" value={values.city} onChange={set('city')} />
        <LabeledText label="State" value={values.state} onChange={set('state')} />
        <LabeledText label="Zip" value={values.zipCode} onChange={set('zipCode')} />
      </FieldRow>
      <CardFooter loading={loading} status={status} error={error} />
    </FieldSection>
  )
}

export default ShippingInfoPanel
